// components/ListaCliente.tsx

import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  ShoppingCart as ShoppingCartIcon,
  Info as InfoIcon,
  MoneyOff as MoneyOffIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Cliente, MENSAGENS } from '@types';
import { SessionUtil } from '@utils';
import { useSessionContext } from '@contexts';
import clienteController from '@controllers/clienteController';

interface ListaClienteProps {
  clientes: Cliente[];
  isSearching?: boolean;
}

export const ListaCliente: React.FC<ListaClienteProps> = ({ clientes, isSearching }) => {
  const navigate = useNavigate();
  const { setCliente } = useSessionContext();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);

  // Obter dados da sessão
  const vendedor = SessionUtil.getVendedor();
  const indPrevisao = vendedor?.indPrevisao || 0;
  const indPedido = vendedor?.indPedido || 0;

  const handleClienteClick = (cliente: Cliente) => {
    // Verifica se está em modo PEDIDO: indPrevisao <> 0 AND indPedido <> 0
    const isModoPedido = indPrevisao !== 0 && indPedido !== 0;

    // Se não está em modo pedido, vai para detalhes
    if (!isModoPedido) {
      navigate(`/cliente/${cliente.codigo}`);
      return;
    }

    // Se está em modo pedido (cliente ativo ou inativo), abre diálogo
    setClienteSelecionado(cliente);
    setDialogOpen(true);
  };

  const handleConfirmarPedido = () => {
    if (clienteSelecionado) {
      // Salvar cliente na sessão usando SessionContext
      setCliente(clienteSelecionado.codigo, clienteSelecionado.nome);

      setDialogOpen(false);

      // Redirecionar para lista de classificações de PEDIDO
      navigate('/classificacoes-pedido');
    }
  };

  const handleCancelarPedido = () => {
    setDialogOpen(false);
    setClienteSelecionado(null);
  };

  const renderAcaoIcone = (cliente: Cliente) => {
    const ativo = clienteController.clienteAtivo(cliente);

    // Cliente com restrição financeira
    if (!ativo) {
      return (
        <IconButton edge="end" sx={{ color: 'error.main' }}>
          <MoneyOffIcon />
        </IconButton>
      );
    }

    // Modo pedido: indPrevisao <> 0 AND indPedido <> 0
    const isModoPedido = indPrevisao !== 0 && indPedido !== 0;
    if (isModoPedido) {
      return (
        <IconButton edge="end" sx={{ color: 'primary.main' }}>
          <ShoppingCartIcon />
        </IconButton>
      );
    }

    // Modo detalhes
    return (
      <IconButton edge="end" sx={{ color: 'info.main' }}>
        <InfoIcon />
      </IconButton>
    );
  };

  return (
    <Box sx={{ pb: 10 }}>
      {/* Lista de Clientes */}
      <List sx={{ px: 1 }}>
        {!clientes || clientes.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {isSearching ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
            </Typography>
          </Box>
        ) : (
          clientes.map((cliente) => {
            const ativo = clienteController.clienteAtivo(cliente);

            return (
              <ListItem
                key={cliente.codigo}
                onClick={() => handleClienteClick(cliente)}
                sx={{
                  mb: 1,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 1,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                    bgcolor: 'action.hover'
                  }
                }}
                secondaryAction={renderAcaoIcone(cliente)}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: ativo ? 'primary.main' : 'error.main' }}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>

                <ListItemText
                  primary={cliente.codigo + ' - ' + cliente.nome}
                  primaryTypographyProps={{
                    variant: 'body1',
                    fontWeight: 'bold'
                  }}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" component="div">
                        {cliente.endereco}
                        {cliente.bairro && `, ${cliente.bairro}`}
                      </Typography>
                      {!ativo && (
                        <Typography variant="caption" color="error" fontWeight="bold" component="div">
                          {MENSAGENS.CLIENTE_SEM_CREDITO}
                        </Typography>
                      )}
                    </>
                  }
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            );
          })
        )}
      </List>

      {/* Diálogo de Confirmação de Pedido */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelarPedido}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {clienteSelecionado && clienteController.clienteAtivo(clienteSelecionado)
            ? 'Iniciar Pedido'
            : 'Cliente Bloqueado'}
        </DialogTitle>
        <DialogContent>
          {clienteSelecionado && clienteController.clienteAtivo(clienteSelecionado) ? (
            <>
              <Typography>
                Deseja iniciar um pedido para o cliente:
              </Typography>
              <Typography fontWeight="bold" sx={{ mt: 2 }}>
                {clienteSelecionado?.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Código: {clienteSelecionado?.codigo}
              </Typography>
            </>
          ) : (
            <>
              <Typography fontWeight="bold" sx={{ mb: 2 }}>
                {clienteSelecionado?.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Código: {clienteSelecionado?.codigo}
              </Typography>
              <Typography color="error" fontWeight="bold">
                {MENSAGENS.CLIENTE_BLOQUEADO}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarPedido} color="inherit">
            {clienteSelecionado && clienteController.clienteAtivo(clienteSelecionado)
              ? 'Cancelar'
              : 'Fechar'}
          </Button>
          {clienteSelecionado && clienteController.clienteAtivo(clienteSelecionado) && (
            <Button onClick={handleConfirmarPedido} variant="contained" color="primary">
              Confirmar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListaCliente;
